import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';

interface Fixture {
  id: string;
  pattern: 'simple' | 'apikey' | 'jwt' | 'sso';
  displayName: string;
}

const PATTERN_LABEL: Record<Fixture['pattern'], string> = {
  simple: 'Pattern 1 — ownerUserId',
  apikey: 'Pattern 2 — apiKey',
  jwt: 'Pattern 3 — JWT',
  sso: 'Pattern 4 — OIDC',
};

export function HomePage() {
  const qc = useQueryClient();
  const [peerHandle, setPeerHandle] = useState('');
  const [lastResult, setLastResult] = useState<unknown>(null);

  const { data } = useQuery({
    queryKey: ['fixtures'],
    queryFn: () => api.get<{ fixtures: Fixture[] }>('/api/fixtures'),
  });

  const signOut = useMutation({
    mutationFn: () => api.post('/api/auth/sign-out'),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      window.location.href = '/login';
    },
  });

  const start = useMutation({
    mutationFn: (fixtureId: string) =>
      api.post<unknown>('/api/fixtures/start-conversation', {
        fixtureId,
        peerHandle,
      }),
    onSuccess: (result) => setLastResult(result),
    onError: (err: Error) => setLastResult({ error: err.message }),
  });

  return (
    <div className="mx-auto max-w-3xl p-8">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Aiport</h1>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/log" className="underline">
            Webhook log
          </Link>
          <button
            type="button"
            onClick={() => signOut.mutate()}
            className="text-neutral-500 hover:text-neutral-900"
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="mt-6">
        <label htmlFor="peer-handle" className="block text-sm font-medium text-neutral-700">
          Plaine peer handle
        </label>
        <input
          id="peer-handle"
          value={peerHandle}
          onChange={(e) => setPeerHandle(e.target.value)}
          placeholder="e.g. dsfaccini"
          className="mt-1 w-full rounded border border-neutral-300 px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-neutral-500">
          The Plaine account that should receive the invite. Typically your own Plaine handle so you
          can accept the invite and drive the flow.
        </p>
      </section>

      <section className="mt-8 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
          Fixture users
        </h2>
        {data?.fixtures.map((f) => (
          <div
            key={f.id}
            className="flex items-center justify-between rounded border border-neutral-200 p-4"
          >
            <div>
              <div className="font-medium">{f.displayName}</div>
              <div className="text-xs text-neutral-500">{PATTERN_LABEL[f.pattern]}</div>
            </div>
            <button
              type="button"
              disabled={!peerHandle || start.isPending}
              onClick={() => start.mutate(f.id)}
              className="rounded bg-neutral-900 px-3 py-1.5 text-sm text-white disabled:opacity-40"
            >
              Start conversation
            </button>
          </div>
        ))}
      </section>

      {lastResult ? (
        <section className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">
            Last attempt
          </h2>
          <pre className="mt-2 overflow-x-auto rounded bg-neutral-900 p-4 text-xs text-neutral-100">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        </section>
      ) : null}
    </div>
  );
}
